import type { BillingPlan } from 'definitions/billing-plans';
import { BILLING_PLAN_CATALOGUE } from 'definitions/billing-plans';
import { ConfirmDialog } from '@/components/Dialogs/ConfirmDialog';
import { formatBillingDate, formatUnits } from '@/state/billing/billing-format';

type KeepPlanDialogProps = {
  open: boolean;
  plan: BillingPlan;
  /** The plan the account is moving up to. */
  nextPlan: BillingPlan;
  appliesAt: string;
  included: number | null;
  /** When the current period ends and the count starts again. */
  windowEnd: string;
  onConfirm: () => void;
  onCancel: () => void;
};

/**
 * Asked before staying on a plan instead of the move up: it is a real decision, so it says what it
 * means. The move is cancelled, the sessions are held to the plan, and sessions over it are ignored until
 * the period ends. The same dialog serves "Keep my plan" in the usage panel and
 * "Keep the current plan" under the plan cards.
 */
export const KeepPlanDialog = ({
  open,
  plan,
  nextPlan,
  appliesAt,
  included,
  windowEnd,
  onConfirm,
  onCancel,
}: KeepPlanDialogProps) => {
  const current = BILLING_PLAN_CATALOGUE[plan].name;
  const next = BILLING_PLAN_CATALOGUE[nextPlan].name;

  return (
    <ConfirmDialog
      open={open}
      title={`Stay on ${current}?`}
      confirmLabel={`Stay on ${current}`}
      onConfirm={onConfirm}
      onCancel={onCancel}
    >
      <div className="space-y-2 text-body">
        <p>
          The move to {next} on {formatBillingDate(appliesAt)} is cancelled, and your plan stays {current}
          {included ? `, with ${formatUnits(included)} sessions per period` : ''}.
        </p>
        <p>
          Sessions over that limit are ignored, across all of Pertento, until the period resets on{' '}
          {formatBillingDate(windowEnd)}. Choosing a larger plan at any time lifts the limit.
        </p>
      </div>
    </ConfirmDialog>
  );
};
